import { OrganizationForm } from "@/forms/organization-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { takeConnectCode } from "@/utils/connect-code-stash";
import { Button } from "@msuite/ui/button";
import { AuthForm } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type OrganizationFormType, organizationSchema } from "@msuite/utils/schemas";
import "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

export function CreateOrganization() {
	/** Hooks */
	const navigate = useNavigate();
	const auth = useAuth();
	const { t } = useTranslation();
	const methods = useForm<OrganizationFormType>({
		resolver: resolver(organizationSchema),
		defaultValues: {
			size: "51-200",
			country: "de",
			color: "indigo",
		},
	});

	/** Functions */
	async function handleOnSubmit(json: OrganizationFormType) {
		try {
			const response = await client.organizations.index.$post({ json });
			await responseHandler(response);
			await auth.refetchAuth();
			// A visitor who arrived by a connect link founds the organisation first; the stash
			// carries the code past sign-up and this form to the connect page.
			const code = takeConnectCode();
			navigate(code ? `/connect/${code}` : "/");
		} catch (error) {
			console.error(error);
			toast.error(t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<div className="flex-center w-full h-full flex-col gap-2">
				<AuthForm
					onSubmit={methods.handleSubmit(handleOnSubmit)}
					data-cy="create-organization-form"
				>
					<AuthForm.Header>
						<h1 className="heading">{t("organization.create_organization")}</h1>
					</AuthForm.Header>
					<AuthForm.Body>
						<OrganizationForm />
					</AuthForm.Body>
					<AuthForm.Footer>
						<Button
							size="sm"
							variant="primary"
							type="submit"
							isLoading={methods.formState.isSubmitting}
						>
							{t("general.create")}
						</Button>
					</AuthForm.Footer>
				</AuthForm>
			</div>
		</FormProvider>
	);
}
